import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from '@mantine/form';
import {
  Grid,
  TextInput,
  Textarea,
  Button,
  Select,
  NumberInput,
  MultiSelect,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import prepareTranslate from '@/helpers/dictionary';
import { ACCORD_TYPE, PRODUCT_ENTITY } from '@/helpers/enums';
import validations from '@/helpers/validation';
import agentMutations from '@/api/mutitations/agent';
import productQuery from '@/api/queries/product';
import companyQuery from '@/api/queries/company';
import s3Mutations from '@/api/mutitations/s3';
import DetailsModal from '@/components/DetailsModal';
import Modal from '@/components/Modal/Index';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';
import TieredCommissionInput from '../TieredCommissionInput';

const AgentAgreementsEdit = (props) => {
  const {
    close,
    opened,
    agreement,
    agentId,
    setKey,
  } = props;

  const t = prepareTranslate();

  const { mutateAsync: uploadMutate } = s3Mutations.useUpload();
  const { mutateAsync: updateAgentItem } = agentMutations.useUpdateAgentItem();
  const { mutateAsync: addAgreementMutate } = agentMutations.usePushToArray();

  const { data: productsData = [] } = productQuery.useGetAll();
  const { data: companiesData } = companyQuery.useGetAll();

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [actionResultOpended, {
    open: actionResultOpen, close: actionResultClose,
  }] = useDisclosure();

  const form = useForm({
    initialValues: {
      agentNumber: '',
      companyId: '',
      branchId: [],
      accordType: '',
      accordName: '',
      depositFrom: null,
      depositUntil: null,
      accumulationFrom: null,
      accumulationUntil: null,
      agentFeeMonthlyDeposit: 0,
      agentFeeAnnualDeposit: [{ from: 0, to: null, rate: 0 }],
      agentFeeMonthlyAccumulation: 0,
      agentFeeAnnualAccumulation: [{ from: 0, to: null, rate: 0 }],
      employeeAgentCommission: 0,
      files: [],
      comments: '',
    },
    validate: {
      agentNumber: (value) => validations.required(value),
      companyId: (value) => validations.required(value),
      branchId: (value) => (value.length === 0 ? validations.required(value) : null),
      accordType: (value) => validations.required(value),
      depositFrom: (value, values) => {
        // eslint-disable-next-line max-len
        const hasDeposit = values.agentFeeMonthlyDeposit > 0 || values.agentFeeMonthlyAccumulation > 0;
        return hasDeposit ? validations.required(value) : null;
      },
      accumulationFrom: (value, values) => {
        const hasMeaningfulTier = (tiers) => Array.isArray(tiers)
          && tiers.some((tier) => tier.rate > 0 || tier.to !== null);
        const hasAccumulation = hasMeaningfulTier(values.agentFeeAnnualDeposit)
          || hasMeaningfulTier(values.agentFeeAnnualAccumulation);
        return hasAccumulation ? validations.required(value) : null;
      },
      agentFeeAnnualDeposit: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
      agentFeeAnnualAccumulation: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
    },
  });

  // eslint-disable-next-line max-len
  const branchOptions = productsData.filter((product) => product.applicableTo.includes(PRODUCT_ENTITY.agreement)).map((branch) => ({
    value: branch._id || branch.id,
    label: branch.hebrew,
  }));

  const companiesOptions = companiesData ? companiesData.map((company) => ({
    value: company._id,
    label: company.hebrew,
  })) : [];

  const accordTypeOptions = ACCORD_TYPE.map((type) => ({
    value: type.value,
    label: type.label,
  }));

  useEffect(() => {
    if (opened && agreement) {
      form.setValues({
        agentNumber: agreement.agentNumber || '',
        companyId: agreement.companyId?._id || agreement.companyId || '',
        branchId: agreement.branchId?.map((b) => b?._id || b) || [],
        accordType: agreement.accordType || '',
        accordName: agreement.accordName || '',
        depositFrom: agreement.depositFrom ? new Date(agreement.depositFrom) : null,
        depositUntil: agreement.depositUntil ? new Date(agreement.depositUntil) : null,
        accumulationFrom: agreement.accumulationFrom ? new Date(agreement.accumulationFrom) : null,
        // eslint-disable-next-line max-len
        accumulationUntil: agreement.accumulationUntil ? new Date(agreement.accumulationUntil) : null,
        agentFeeMonthlyDeposit: agreement.agentFeeMonthlyDeposit || 0,
        agentFeeAnnualDeposit: Array.isArray(agreement.agentFeeAnnualDeposit)
          ? agreement.agentFeeAnnualDeposit
          : [{ from: 0, to: null, rate: 0 }],
        agentFeeMonthlyAccumulation: agreement.agentFeeMonthlyAccumulation || 0,
        agentFeeAnnualAccumulation: Array.isArray(agreement.agentFeeAnnualAccumulation)
          ? agreement.agentFeeAnnualAccumulation
          : [{ from: 0, to: null, rate: 0 }],
        employeeAgentCommission: agreement.employeeAgentCommission || 0,
        files: agreement.files || [],
        comments: agreement.comments || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, agreement]);

  // Check if there are deposit fees
  const hasDepositFees = form.values.agentFeeMonthlyDeposit > 0
    || form.values.agentFeeMonthlyAccumulation > 0 || form.values.employeeAgentCommission > 0;

  // Check if there are accumulation fees
  const hasMeaningfulTier = (tiers) => Array.isArray(tiers)
    && tiers.some((tier) => tier.rate > 0 || tier.to !== null);
  const hasAccumulationFees = hasMeaningfulTier(form.values.agentFeeAnnualDeposit)
    || hasMeaningfulTier(form.values.agentFeeAnnualAccumulation);

  const cleanAgreementData = () => {
    const values = form.getValues();

    const cleaned = {
      ...values,
      companyId: typeof values.companyId === 'object' ? values.companyId._id : values.companyId,
      branchId: (values.branchId || []).map((b) => b._id || b),
    };

    if (cleaned.accordType === 'employee') {
      cleaned.agentFeeMonthlyDeposit = 0;
      cleaned.agentFeeMonthlyAccumulation = 0;
    } else if (cleaned.accordType === 'independent') {
      cleaned.employeeAgentCommission = 0;
    }

    return cleaned;
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const newData = cleanAgreementData();
      let uploadFiles;
      const newFiles = newData.files.filter((file) => !file.path);

      if (newFiles && newFiles.length > 0) {
        const formData = new FormData();

        newFiles.forEach((file) => {
          const realFile = file.file || file;
          formData.append('files', realFile, file.name);
        });

        try {
          uploadFiles = await uploadMutate(formData);
          const oldFiles = form.values.files.filter((file) => file.path);
          newData.files = [...uploadFiles.files, ...oldFiles];
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          setIsLoading(false);
          return;
        }
      }

      const old = agreement;

      const origDepositTime = old.depositFrom ? new Date(old.depositFrom).getTime() : null;
      const origAccumulationTime = old.accumulationFrom
        ? new Date(old.accumulationFrom).getTime()
        : null;

      const {
        depositFrom,
        accumulationFrom,
        agentFeeMonthlyDeposit,
        agentFeeMonthlyAccumulation,
        employeeAgentCommission,
        agentFeeAnnualDeposit,
        agentFeeAnnualAccumulation,
      } = newData;

      const datesChanged = (
        depositFrom ? new Date(depositFrom).getTime() : null) !== origDepositTime
      || (accumulationFrom ? new Date(accumulationFrom).getTime() : null) !== origAccumulationTime;

      const commissionsChanged = old.agentFeeMonthlyDeposit !== agentFeeMonthlyDeposit
        || old.agentFeeMonthlyAccumulation !== agentFeeMonthlyAccumulation
        || old.employeeAgentCommission !== employeeAgentCommission
        || JSON.stringify(old.agentFeeAnnualDeposit) !== JSON.stringify(agentFeeAnnualDeposit)
        || JSON.stringify(
          old.agentFeeAnnualAccumulation,
        ) !== JSON.stringify(agentFeeAnnualAccumulation);

      const needToArchive = datesChanged && commissionsChanged;

      if (needToArchive) {
        const oldHasDates = old.depositFrom || old.accumulationFrom;
        const oldDepositRef = old.depositFrom ? new Date(old.depositFrom) : null;
        const oldAccumulationRef = old.accumulationFrom ? new Date(old.accumulationFrom) : null;
        // Compare each date field against its own original value (not a single combined ref)
        // If old has no formal dates at all, treat any new dated agreement as historical → archive
        const newIsEarlier = !oldHasDates || (
          (depositFrom && oldDepositRef && new Date(depositFrom) < oldDepositRef)
          // eslint-disable-next-line max-len
          || (accumulationFrom && oldAccumulationRef && new Date(accumulationFrom) < oldAccumulationRef)
        );

        if (newIsEarlier) {
          await addAgreementMutate({
            value: { ...newData, status: 'archive', archiveAt: new Date() },
            id: agentId,
            key: 'agreements',
          });
        } else {
          const archived = {
            ...old,
            status: 'archive',
            archiveAt: new Date(),
          };

          if (newData.depositFrom || newData.accumulationFrom) {
            const depositUntilCandidate = old.depositUntil || newData.depositFrom;
            const accumulationUntilCandidate = old.accumulationUntil || newData.accumulationFrom;

            // eslint-disable-next-line max-len
            if (depositUntilCandidate && (!old.depositFrom || new Date(depositUntilCandidate) > new Date(old.depositFrom))) {
              archived.depositUntil = depositUntilCandidate;
            }
            // eslint-disable-next-line max-len
            if (accumulationUntilCandidate && (!old.accumulationFrom || new Date(accumulationUntilCandidate) > new Date(old.accumulationFrom))) {
              archived.accumulationUntil = accumulationUntilCandidate;
            }
          }

          await updateAgentItem({
            agentId,
            itemId: old._id,
            key: 'agreements',
            data: archived,
          });

          await addAgreementMutate({
            value: { ...newData, status: 'active' },
            id: agentId,
            key: 'agreements',
          });
        }
      } else {
        await updateAgentItem({
          agentId,
          itemId: old._id,
          key: 'agreements',
          data: newData,
        });
      }

      setResultLabel(t('components.viewAgentAgreements.labels.success.update'));
      actionResultOpen();
      setKey((prev) => prev + 1);
    } catch (err) {
      setResultLabel(t('components.viewAgentAgreements.labels.errors.update'));
      actionResultOpen();
      form.errors = null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    setDeleteLoading(true);
    try {
      const updatedAgreement = {
        ...agreement,
        status: 'archive',
        ...cleanAgreementData(),
        id: agreement._id,
        archiveAt: new Date(),
        deletionReason: form.values.deletionReason,
      };
      await updateAgentItem({
        agentId,
        itemId: agreement.id || agreement._id,
        key: 'agreements',
        data: updatedAgreement,
      });
      setKey((prev) => prev + 1);
      setResultLabel(t('components.editContracts.successDelete'));
      actionResultOpen();
    } catch (err) {
      setResultLabel(t('components.editContracts.failureDelete'));
      actionResultOpen();
      form.errors = null;
    } finally {
      setDeleteLoading(false);
    }
  };

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const actions = [
    <Button key='save' loading={isLoading} onClick={form.onSubmit(handleSave)}>
      {t('global.save')}
    </Button>,
    <Button key='cancel' variant='outline' onClick={close}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button key='close' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const onFileNameChange = (index, newName) => {
    const updated = [...form.getValues().files];
    updated[index] = { ...updated[index], name: newName };

    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };

  const handleCancel = () => {
    closeDelete();
  };

  const handleDeleteAgreement = () => {
    if (!form.values.deletionReason) {
      form.setFieldError('deletionReason', 'שדה חובה');
      return;
    }

    handleArchive(form.values.deletionReason);
    closeDelete();
  };

  const deleteActions = [
    <Button key='delete' w={'6rem'} onClick={handleDeleteAgreement} loading={deleteLoading}>
      {t('global.delete')}
    </Button>,
    <Button key='cancel' w={'6rem'} onClick={handleCancel} variant='outline'>
      {t('global.cancel')}
    </Button>,
  ];

  const modalLeftActions = [
    <Button
      key='delete'
      variant='outline'
      onClick={openDelete}
    >
      {t('components.viewAgentAgreements.labels.delete')}
    </Button>,
  ];

  const depositUntilMinDate = form.values.depositFrom
    ? new Date(
      form.values.depositFrom.getFullYear(),
      form.values.depositFrom.getMonth() + 1,
      1,
    ) : undefined;

  const accumulationUntilMinDate = form.values.accumulationFrom
    ? new Date(
      form.values.accumulationFrom.getFullYear(),
      form.values.accumulationFrom.getMonth() + 1,
      1,
    ) : undefined;

  return (
    <>
      <DetailsModal
        opened={opened}
        close={close}
        label={'עריכת הסכם'}
        size='90%'
        actions={actions}
        leftActions={modalLeftActions}
      >
        <Grid gutter='md'>
          <Grid.Col span={3}>
            <TextInput
              label={t('components.viewAgentAgreements.labels.agentnumber')}
              required
              {...form.getInputProps('agentNumber')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <Select
              label={'חברה'}
              data={companiesOptions}
              required
              searchable
              {...form.getInputProps('companyId')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <MultiSelect
              label={t('components.editContracts.branch')}
              data={branchOptions}
              required
              {...form.getInputProps('branchId')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <Select
              label={t('components.viewAgentAgreements.labels.accordType')}
              data={accordTypeOptions}
              required
              {...form.getInputProps('accordType')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <MonthPickerInput
              maxDate={new Date()}
              label={t('components.viewAgentAgreements.labels.date.depositFrom')}
              required={hasDepositFees}
              {...form.getInputProps('depositFrom')}
              onChange={(value) => {
                form.setFieldValue('depositFrom', value);
                if (value && form.values.depositUntil && value > form.values.depositUntil) {
                  form.setFieldValue('depositUntil', null);
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <MonthPickerInput
              disabled={!form.values.depositFrom}
              minDate={depositUntilMinDate}
              label={t('components.viewAgentAgreements.labels.date.depositUntil')}
              {...form.getInputProps('depositUntil')}
            />
          </Grid.Col>

          {form.values.accordType === 'independent' ? (
            <>
              <Grid.Col span={3}>
                <NumberInput
                  label={t('components.viewAgentAgreements.labels.commission.agentFeeMonthlyDeposit')}
                  {...form.getInputProps('agentFeeMonthlyDeposit')}
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <NumberInput
                  label={t('components.viewAgentAgreements.labels.commission.agentFeeMonthlyAccumulation')}
                  {...form.getInputProps('agentFeeMonthlyAccumulation')}
                />
              </Grid.Col>
            </>
          ) : (
            <Grid.Col span={3}>
              <NumberInput
                label={t('components.viewAgentAgreements.labels.commission.employeeAgentCommission')}
                {...form.getInputProps('employeeAgentCommission')}
              />
            </Grid.Col>
          )}

          <Grid.Col span={3}>
            <MonthPickerInput
              maxDate={new Date()}
              label={t('components.viewAgentAgreements.labels.date.accumulationFrom')}
              required={hasAccumulationFees}
              {...form.getInputProps('accumulationFrom')}
              onChange={(value) => {
                form.setFieldValue('accumulationFrom', value);
                // eslint-disable-next-line max-len
                if (value && form.values.accumulationUntil && value > form.values.accumulationUntil) {
                  form.setFieldValue('accumulationUntil', null);
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <MonthPickerInput
              disabled={!form.values.accumulationFrom}
              minDate={accumulationUntilMinDate}
              label={t('components.viewAgentAgreements.labels.date.accumulationUntil')}
              {...form.getInputProps('accumulationUntil')}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('components.editContracts.commision.agentFeeAnnualDeposit')}
              {...form.getInputProps('agentFeeAnnualDeposit')}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('components.editContracts.commision.agentFeeAnnualAccumulation')}
              {...form.getInputProps('agentFeeAnnualAccumulation')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <TextInput
              label={t('components.viewAgentAgreements.labels.accordName')}
              {...form.getInputProps('accordName')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <Textarea
              label={t('components.editContracts.comments')}
                maxLength={500}
                minRows={3}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <UploadFilesButton
              onFilesSelected={onFileSelected}
              label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
            />
            {form.values.files?.length > 0 && (
              <EditFiles
                files={form.values.files}
                onRemove={onDeleteFile}
                onNameChange={onFileNameChange}
              />
            )}

          </Grid.Col>

        </Grid>
      </DetailsModal>

      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        label={resultLabel}
        actions={resultActions}
      />
      <DetailsModal
        opened={deleteOpened}
        close={handleCancel}
        label={t('components.editContracts.verifyDeletion')}
        actions={deleteActions}
      >
        <TextInput
          required
          {...form.getInputProps('deletionReason')}
          label={t('components.editClients.deletionReason')}
        />
      </DetailsModal>

    </>
  );
};

AgentAgreementsEdit.propTypes = {
  opened: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  agreement: PropTypes.object,
  agentId: PropTypes.string.isRequired,
  setKey: PropTypes.func,
  onSave: PropTypes.func,
};

export default AgentAgreementsEdit;
